import { useCallback, useMemo, useState } from "react";
import type { TripDetailsV2 } from "@b2bportal/air-shopping-api";
import {
  CallState,
  type FlightFaresSourceResult,
  type FlightFaresExternalResource,
  type FlightFareMeta,
  type IPassengerCounts,
  type FlightFareSelectionFunctionParams,
  type IFareTrip,
} from "@b2bportal/core-types";
import {
  type FlightFareTransformFn,
  useFlightFareSources,
} from "./FareSourcesProvider";

export interface UseFlightFaresProps {
  trip: TripDetailsV2;
  passengers: IPassengerCounts;
  fareTrips?: IFareTrip[];
  onFareSelect: (
    params: Omit<FlightFareSelectionFunctionParams, "sliceId" | "tripId">
  ) => void;
}

export const useFlightFares = ({
  trip,
  passengers,
  fareTrips,
  onFareSelect,
}: UseFlightFaresProps): FlightFaresExternalResource => {
  const { hooks, transform, setDeselector, deselect } = useFlightFareSources();
  const [selectedFare, setSelectedFareState] = useState<FlightFareMeta | null>(
    null
  );

  const setSelectedFare = useCallback(
    (fare: FlightFareMeta) => {
      if (fare.origin !== "cfar") {
        deselect();
      }
      setSelectedFareState(fare);
      onFareSelect({ fareId: fare.fareDetails.id });

      const deselector = fare.setSelectedFare();
      if (typeof deselector === "function") {
        setDeselector(deselector);
      }
    },
    [deselect, setDeselector, onFareSelect]
  );

  const sourceResults = hooks.map((hook) =>
    hook({ trip, passengers, fareTrips })
  );

  return useMergeResults(
    sourceResults,
    transform,
    selectedFare,
    setSelectedFare
  );
};

const useMergeResults = (
  sourceResults: FlightFaresSourceResult[],
  transform: FlightFareTransformFn,
  selectedFare: FlightFareMeta | null,
  setSelectedFare: (fare: FlightFareMeta) => void
): FlightFaresExternalResource => {
  const isFetching = useMemo(
    () =>
      sourceResults.some(
        ({ state }) =>
          state === CallState.InProcess || state === CallState.NotCalled
      ),
    [sourceResults]
  );

  const fares = useMemo(() => {
    const allFares = sourceResults
      .map((result) => (result.state === CallState.Success ? result.data : []))
      .flat();

    return transform(allFares);
  }, [sourceResults, transform]);

  if (isFetching) {
    return { state: CallState.InProcess };
  }

  return {
    state: CallState.Success,
    data: {
      fares,
      selectedFare,
      setSelectedFare,
    },
  };
};
